<template>
  <div
    ref="rootRef"
    class="scroll-area"
    :class="{ 'more-left': fade && hasMoreLeft, 'more-right': fade && hasMoreRight }"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { useEventListener, useMutationObserver, useResizeObserver } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

/** `fade` softens whichever edge still has content out of view, so a cut-off item reads as scrollable */
const props = defineProps<{ fade?: boolean }>();

const rootRef = ref<HTMLElement>();
const hasMoreLeft = ref(false);
const hasMoreRight = ref(false);
// The row overflows because of what is inside it, so the children are watched as well as the box
const measuredElements = computed<HTMLElement[]>(() =>
  rootRef.value ? [rootRef.value, ...(Array.from(rootRef.value.children) as HTMLElement[])] : [],
);

function update() {
  const element = rootRef.value;

  if (!props.fade || !element) {
    hasMoreLeft.value = false;
    hasMoreRight.value = false;

    return;
  }

  const maxScroll = element.scrollWidth - element.clientWidth;
  hasMoreLeft.value = element.scrollLeft > 1;
  hasMoreRight.value = element.scrollLeft < maxScroll - 1;
}

useEventListener(rootRef, 'scroll', update, { passive: true });
useResizeObserver(measuredElements, update);
// Tabs and chips arrive with their data, long after this has mounted
useMutationObserver(rootRef, update, { childList: true, subtree: true, characterData: true });
// Text is measured in the fallback font until the web font lands, which changes the overflow
document.fonts?.ready.then(update);

watch([() => props.fade, rootRef], update, { immediate: true, flush: 'post' });
</script>

<style scoped lang="scss">
.scroll-area {
  --scroll-area-fade: 2.5rem;

  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  // The gradient follows the scroll position: only a side with content out of view is softened
  &.more-right:not(.more-left) {
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - var(--scroll-area-fade)), transparent);
    mask-image: linear-gradient(to right, #000 calc(100% - var(--scroll-area-fade)), transparent);
  }

  &.more-left:not(.more-right) {
    -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--scroll-area-fade));
    mask-image: linear-gradient(to right, transparent, #000 var(--scroll-area-fade));
  }

  &.more-left.more-right {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--scroll-area-fade),
      #000 calc(100% - var(--scroll-area-fade)),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--scroll-area-fade),
      #000 calc(100% - var(--scroll-area-fade)),
      transparent
    );
  }
}
</style>
